import type {
  HottrReportInsert,
  HottrModerationActionInsert,
  HottrUserSanctionRow,
  ModerationAction,
  ReportContextKind,
} from '@/lib/database.types';

export const REPORT_REASONS = [
  'fake_profile',
  'underage',
  'harassment',
  'spam_or_scam',
  'inappropriate_photo',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export interface SubmitReportInput {
  reportedUserId: string;
  reason: ReportReason;
  detail?: string;
  contextKind?: ReportContextKind;
  contextRef?: string | null;
}

export type BuildReportResult =
  | { ok: true; row: Omit<HottrReportInsert, 'id' | 'created_at'> }
  | { ok: false; error: string };

const DETAIL_MAX = 1000;

/**
 * Build the `hottr_reports` insert row from validated input, or fail loud
 * on self-report. Detail is trimmed and capped — moderators see a clean
 * payload regardless of free-text length.
 */
export const buildReportInsert = (
  input: SubmitReportInput,
  currentUserId: string,
): BuildReportResult => {
  if (input.reportedUserId === currentUserId) {
    return { ok: false, error: 'You cannot report yourself.' };
  }
  const trimmed = input.detail?.trim() ?? '';
  return {
    ok: true,
    row: {
      reporter_id: currentUserId,
      reported_id: input.reportedUserId,
      reason: input.reason,
      detail: trimmed ? trimmed.slice(0, DETAIL_MAX) : null,
      context_kind: input.contextKind ?? 'profile',
      context_ref: input.contextRef ?? null,
    },
  };
};

export interface ApplyActionInput {
  targetUserId: string;
  action: ModerationAction;
  notes?: string;
  relatedReport?: string | null;
  /** Hours from now this sanction expires. Omit for permanent. */
  expiresHours?: number;
}

/**
 * Build the `hottr_moderation_actions` insert row. Pure given the
 * moderator id + a reference `now` (ms epoch) so expiry is testable.
 */
export const buildModerationActionInsert = (
  input: ApplyActionInput,
  moderatorId: string,
  now: number,
): Omit<HottrModerationActionInsert, 'id' | 'created_at'> => {
  const expires_at = input.expiresHours
    ? new Date(now + input.expiresHours * 3600_000).toISOString()
    : null;
  const trimmedNotes = input.notes?.trim();
  return {
    moderator_id: moderatorId,
    target_user_id: input.targetUserId,
    action: input.action,
    notes: trimmedNotes ? trimmedNotes : null,
    related_report: input.relatedReport ?? null,
    expires_at,
  };
};

type SanctionLite = Pick<HottrUserSanctionRow, 'user_id' | 'sanction'>;

/**
 * Build the set of user ids whose profiles should be hidden — those
 * holding an active `suspended` or `banned` sanction. `warned` and
 * `throttled` remain visible; throttling is a feed-rank concern, not a
 * visibility concern.
 */
export const sanctionsToBannedIds = (
  rows: SanctionLite[] | null | undefined,
): Set<string> => {
  const out = new Set<string>();
  if (!rows) return out;
  for (const r of rows) {
    if (r.sanction === 'suspended' || r.sanction === 'banned') {
      out.add(r.user_id);
    }
  }
  return out;
};

/* Re-exports kept for compatibility with the existing useReport callers. */
export const REPORT_REASON_LABEL: Record<ReportReason, string> = {
  fake_profile: 'Fake profile',
  underage: 'Underage',
  harassment: 'Harassment or threats',
  spam_or_scam: 'Spam or scam',
  inappropriate_photo: 'Inappropriate photo',
  other: 'Something else',
};
